import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BasicTable, Button } from '@fullscreendigital/carrefour-dex-fe'
import s from '../../../../commonStyles.modules.scss'
import { getColumns, getDownloadUrl, toLitigationDataPayload } from './helpers'
import { makeCoreGetFileRequest, makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { formatAndDownloadResponse, handleQueryError } from 'utils/helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { useMutation, useQuery } from 'react-query'
import { toResponsibleOptions } from './serializers'
import { ConfirmationModal } from './ConfirmationModal'
import { getScreenPermissions } from 'components/shop-reception/admin/api'
import { SCREENS } from 'components/shop-reception/admin/constants'

const Details = ({
  list,
  showMessage,
  depositUserClassified,
  refreshData,
  showAccept,
  litigationId,
  shopUserGenerated,
  type,
  showResponsibility
}) => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)

  const { data: permissions } = getScreenPermissions(
    showNotification,
    SCREENS.PACKAGING_RETURN_LITIGATION
  )

  const downloadImages = async (supportId, type) => {
    try {
      const url = getDownloadUrl(supportId, type)
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      formatAndDownloadResponse(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la descarcarea imaginilor. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const mutateAccepted = async ({ value, supportId }) => {
    const val = value.value === 'DA'
    await makeCorePostRequest(
      `/shop-reception/return-litigation/details/supports/${supportId}/save-accept?option=${val}`
    )
  }

  const { mutate } = useMutation(mutateAccepted, {
    onSuccess: () => {
      showNotification({
        message: 'Schimbarile au fost salvate cu succes',
        type: 'success'
      })
      refreshData()
    },
    onError: error =>
      handleQueryError(error, 'A aparut o eroare la salvarea campului Accepta.', showNotification)
  })

  const { data: responsibleOptions } = useQuery(
    ['responsible'],
    async () => {
      const result = await makeCoreGetRequest(
        '/shop-reception/return-litigation/details/responsible'
      )
      return toResponsibleOptions(result)
    },
    {
      onError: error => {
        showNotification({
          message: `A apărut o eroare la obtinerea detaliilor litigiului. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      }
    }
  )

  const mutateResponsible = async ({ value, supportId }) => {
    await makeCorePostRequest(
      `/shop-reception/return-litigation/details/supports/${supportId}/save-deposit-option?responsibilityType=${value}`
    )
  }

  const { mutate: updateResponsible } = useMutation(mutateResponsible, {
    onSuccess: () => {
      showNotification({
        message: 'Schimbarile au fost salvate cu succes',
        type: 'success'
      })
      refreshData()
    },
    onError: error =>
      handleQueryError(
        error,
        'A aparut o eroare la salvarea campului Responsabil.',
        showNotification
      )
  })

  const uploadImages = rowId =>
    navigate(`/shop-reception/package/storeReturnsDisputesImages/${rowId}`)

  const columns = useMemo(
    () =>
      getColumns({
        downloadImages,
        showMessage,
        updateAccepted: mutate,
        showResponsibility,
        depositUserClassified,
        responsibleOptions,
        updateResponsible,
        showAccept,
        uploadImages
      }),
    [responsibleOptions, showMessage, permissions?.canView]
  )

  const openConfirmationModal = (): void => setIsConfirmationModalOpen(true)
  const closeConfirmationModal = (): void => setIsConfirmationModalOpen(false)

  const mutateData = async () => {
    await makeCorePostRequest(
      `/shop-reception/return-litigation/details/${litigationId}`,
      toLitigationDataPayload(list)
    )
  }

  const { mutate: saveData } = useMutation(mutateData, {
    onSuccess: () => {
      showNotification({
        message: 'Schimbarile au fost salvate cu succes',
        type: 'success'
      })
      refreshData()
      closeConfirmationModal()
    },
    onError: error =>
      handleQueryError(error, 'A aparut o eroare la salvarea datelor.', showNotification)
  })

  const disabledButton = list.some(row => {

    if (showAccept && row.canUpload && (row.accepted !== true && row.accepted !== false)) {
      return true;
    }

    if (!row.hasShopImages && row.canUpload && row.noImages === true && row.accepted !== true) {
      return true;
    }

    return false;
  });

  return (
    <>
      <BasicTable
        columns={columns}
        rows={list}
        containerProps={{
          className: s.table
        }}
      />
      {type === 'in_progress' && (shopUserGenerated || depositUserClassified) && (
        <Button
          variant="contained"
          label="Salveaza si trimite"
          onClick={openConfirmationModal}
          style={{ marginTop: '40px', float: 'right' }}
          disabled={disabledButton}
        />
      )}
      <ConfirmationModal
        isModalOpen={isConfirmationModalOpen}
        closeModal={closeConfirmationModal}
        saveData={saveData}
        shopUser={shopUserGenerated}
        depositUser={depositUserClassified}
      />
    </>
  )
}

export default Details
